// src/pages/Dashboard/Tabs/TabResumenCompras.js
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import KpiCard from '../../../components/Dashboard/KpiCard';
import ComprasChart from '../../../components/Dashboard/ComprasChart';
import DrilldownModal from '../../../components/Dashboard/DrilldownModal';
import FilterBar from '../../../components/Dashboard/FilterBar';
import { formatMoney, formatMoneyCorto, formatNumber, formatMes, formatFecha } from '../../../utils/formato';

/**
 * Define las 6 tarjetas del resumen a partir de los KPIs que devuelve el
 * backend. `key` tiene que coincidir con lo que espera
 * GET /compras/dashboard/resumen/detalle?kpi=...
 */
const construirKpis = (kpis) => [
  {
    key: 'ordenesTotales',
    title: 'Órdenes del Período',
    value: formatNumber(kpis.ordenesTotales),
    icon: 'bi-file-earmark-text',
    color: 'blue'
  },
  {
    key: 'montoTotal',
    title: 'Monto Comprado',
    value: formatMoneyCorto(kpis.montoTotal),
    // Sale de DocTotal, que ya incluye impuestos.
    subtitle: `${formatMoney(kpis.montoTotal)} · con IVA`,
    icon: 'bi-cash-coin',
    color: 'green'
  },
  {
    key: 'proveedoresActivos',
    title: 'Proveedores Activos',
    value: formatNumber(kpis.proveedoresActivos),
    icon: 'bi-people-fill',
    color: 'black'
  },
  {
    key: 'ordenesAbiertas',
    title: 'Órdenes Abiertas',
    value: formatNumber(kpis.ordenesAbiertas),
    subtitle: kpis.ordenesTotales
      ? `${Math.round((kpis.ordenesAbiertas / kpis.ordenesTotales) * 100)}% del período`
      : null,
    icon: 'bi-hourglass-split',
    color: 'blue'
  },
  {
    key: 'montoPendiente',
    title: 'Monto Pendiente',
    value: formatMoneyCorto(kpis.montoPendiente),
    subtitle: formatMoney(kpis.montoPendiente),
    icon: 'bi-clock-history',
    color: 'red'
  },
  {
    key: 'ordenesVencidas',
    title: 'Órdenes Vencidas',
    value: formatNumber(kpis.ordenesVencidas),
    subtitle: kpis.ordenesVencidas > 0 ? 'Requieren seguimiento' : 'Ninguna vencida',
    icon: 'bi-exclamation-triangle-fill',
    color: kpis.ordenesVencidas > 0 ? 'red' : 'green'
  }
];

// La empresa la elige el Dashboard y baja como prop.
const TabResumenCompras = ({ empresa = process.env.REACT_APP_EMPRESA_DEFAULT || 'CIMSA' }) => {

  const [filters, setFilters] = useState({ dateRange: '30d', provider: '' });
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [resumen, setResumen] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalState, setModalState] = useState({ show: false, title: '', kpiKey: null });
  const [modalData, setModalData] = useState([]);
  const [isModalLoading, setIsModalLoading] = useState(false);

  // Los filtros aplicados se leen al disparar la carga, no en cada tecleo:
  // por eso el efecto depende de fetchTrigger y no de `filters`.
  const paramsActuales = useCallback(() => ({
    empresa,
    rango: filters.dateRange,
    proveedor: filters.provider || undefined
  }), [empresa, filters]);

  useEffect(() => {
    let cancelado = false;

    const cargarResumen = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const { data } = await api.get('/compras/dashboard/resumen', { params: paramsActuales() });
        if (!cancelado) setResumen(data);
      } catch (err) {
        if (!cancelado) {
          console.error('Error cargando el resumen de compras:', err);
          setError(err.response?.data?.error || 'No se pudo cargar el resumen de compras.');
        }
      } finally {
        if (!cancelado) {
          setIsLoading(false);
          setIsFilterLoading(false);
        }
      }
    };

    cargarResumen();
    // Evita aplicar la respuesta de una consulta vieja si el usuario ya cambió el filtro.
    return () => { cancelado = true; };

    // paramsActuales cambia con cada tecleo del filtro; la recarga se dispara
    // explícitamente con el botón "Aplicar", de ahí que sólo dependa del trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchTrigger, empresa]);

  const handleApplyFilters = () => {
    setIsFilterLoading(true);
    setFetchTrigger(t => t + 1);
  };

  const handleKpiClick = async (kpiKey, title) => {
    setModalState({ show: true, title: `Detalle de: ${title}`, kpiKey });
    setIsModalLoading(true);
    setModalData([]);

    try {
      const { data } = await api.get('/compras/dashboard/resumen/detalle', {
        params: { ...paramsActuales(), kpi: kpiKey }
      });
      setModalData(data);
    } catch (err) {
      console.error('Error cargando el detalle del KPI:', err);
      setModalData([]);
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setModalState({ show: false, title: '', kpiKey: null });
    setModalData([]);
  };

  if (isLoading && fetchTrigger === 0) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-2">Cargando Resumen de Compras...</p>
      </div>
    );
  }

  const kpis = resumen?.kpis;

  const datosTopProveedores = resumen && {
    labels: resumen.topProveedores.labels,
    datasets: [{ label: 'Monto Total', data: resumen.topProveedores.data, backgroundColor: '#001C7D', borderRadius: 4 }]
  };

  const datosEvolucion = resumen && {
    labels: resumen.evolucionMensual.labels.map(formatMes),
    datasets: [{
      label: 'Monto Comprado',
      data: resumen.evolucionMensual.data,
      borderColor: '#1A3BFF',
      backgroundColor: 'rgba(26, 59, 255, 0.12)',
      fill: true,
      tension: 0.3,
      pointRadius: 4
    }]
  };

  return (
    <div className="mt-4">
      <FilterBar
        filters={filters}
        setFilters={setFilters}
        onApply={handleApplyFilters}
        isLoading={isFilterLoading}
      />

      {error && <div className="alert alert-danger mt-4">{error}</div>}

      {isLoading && fetchTrigger > 0 && (
        <div className="text-center my-4">
          <div className="spinner-border text-primary" role="status"></div>
          <p>Actualizando Resumen de Compras...</p>
        </div>
      )}

      {!isLoading && !error && kpis && (
        <>
          {resumen.periodo && (
            <p className="text-muted small mb-2">
              <i className="bi bi-building me-1"></i>
              Empresa: <strong>{empresa}</strong>
              <span className="mx-2">·</span>
              <i className="bi bi-calendar3 me-1"></i>
              Período: {formatFecha(resumen.periodo.desde)} — {formatFecha(resumen.periodo.hasta)}
              {filters.provider && <> · Proveedor: <strong>{filters.provider}</strong></>}
            </p>
          )}

          <div className="row">
            {construirKpis(kpis).map(kpi => (
              <KpiCard
                key={kpi.key}
                kpiKey={kpi.key}
                title={kpi.title}
                value={kpi.value}
                subtitle={kpi.subtitle}
                icon={kpi.icon}
                color={kpi.color}
                onClick={handleKpiClick}
              />
            ))}
          </div>

          <div className="row mt-2">
            <div className="col-lg-7 mb-3">
              <div className="card shadow-sm h-100">
                <div className="card-body">
                  <ComprasChart
                    chartData={datosTopProveedores}
                    title="Top 10 Proveedores por Monto"
                    horizontal
                    height={420}
                  />
                </div>
              </div>
            </div>
            <div className="col-lg-5 mb-3">
              <div className="card shadow-sm h-100">
                <div className="card-body">
                  <ComprasChart
                    chartData={datosEvolucion}
                    title="Evolución Mensual"
                    tipo="line"
                    height={420}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <DrilldownModal
        show={modalState.show}
        handleClose={handleCloseModal}
        title={modalState.title}
        isLoading={isModalLoading}
        ocData={modalData}
        entradasData={[]}
      />
    </div>
  );
};

export default TabResumenCompras;
